import React, { useState } from 'react';
import { Plus, Building2, MapPin, User, Package, Pencil, PowerOff } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import Modal from '../../components/ui/Modal';
import { useWarehouse } from '../../context/WarehouseContext';
import { useAuth } from '../../context/AuthContext';

const emptyForm = {
  name: '', code: '', city: '',
  address: { street: '', state: '', pincode: '' },
  contactPerson: '', contactPhone: '',
  racks: 'A,B,C',
};

const WarehouseCard = ({ wh, onEdit, onToggle, canManage }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    className={`zip-card p-5 flex flex-col gap-4 transition-opacity ${wh.isActive ? '' : 'opacity-60'}`}
  >
    {/* Header row */}
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-zip-red/10 border border-zip-red/20 flex items-center justify-center flex-shrink-0">
          <Building2 size={18} className="text-zip-red" />
        </div>
        <div>
          <div className="font-semibold text-zip-text text-sm leading-tight">{wh.name}</div>
          <div className="text-[11px] font-mono text-zip-muted mt-0.5">{wh.code}</div>
        </div>
      </div>
      <span className={wh.isActive ? 'chip-green' : 'chip-gray'}>
        {wh.isActive ? 'Active' : 'Inactive'}
      </span>
    </div>

    {/* Details */}
    <div className="space-y-1.5 text-xs text-zip-muted">
      <div className="flex items-start gap-2">
        <MapPin size={12} className="mt-0.5 flex-shrink-0 text-zip-red/70" />
        <span>{[wh.city, wh.address?.state, wh.address?.pincode].filter(Boolean).join(', ')}</span>
      </div>
      {wh.address?.street && (
        <div className="flex items-start gap-2 pl-[20px]">
          <span className="text-zip-muted/80">{wh.address.street}</span>
        </div>
      )}
      {wh.contactPerson && (
        <div className="flex items-center gap-2">
          <User size={12} className="flex-shrink-0 text-zip-red/70" />
          <span>{wh.contactPerson}</span>
          {wh.contactPhone && <span className="font-mono">· {wh.contactPhone}</span>}
        </div>
      )}
      <div className="flex items-center gap-2">
        <Package size={12} className="flex-shrink-0 text-zip-red/70" />
        <span>Racks: <span className="font-mono text-zip-text">{wh.racks?.join(', ') || '—'}</span></span>
      </div>
    </div>

    {/* Actions */}
    {canManage && (
      <div className="flex items-center gap-2 pt-1 border-t border-zip-border">
        <button
          onClick={() => onEdit(wh)}
          className="flex items-center gap-1.5 text-xs font-medium text-zip-muted hover:text-zip-text transition-colors px-2 py-1 rounded hover:bg-zip-hover"
        >
          <Pencil size={12} /> Edit
        </button>
        <button
          onClick={() => onToggle(wh)}
          className={`flex items-center gap-1.5 text-xs font-medium transition-colors px-2 py-1 rounded
            ${wh.isActive ? 'text-zip-muted hover:text-zip-red hover:bg-red-50' : 'text-emerald-600 hover:bg-emerald-50'}`}
        >
          <PowerOff size={12} />
          {wh.isActive ? 'Deactivate' : 'Activate'}
        </button>
      </div>
    )}
  </motion.div>
);

const WarehouseList = () => {
  const { warehouses, refetchWarehouses } = useWarehouse();
  const { can } = useAuth();
  const canManage = can('warehouses.manage');
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const displayed = showAll ? warehouses : warehouses.filter((w) => w.isActive);

  const openCreate = () => {
    setEditTarget(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (wh) => {
    setEditTarget(wh);
    setForm({
      name: wh.name,
      code: wh.code,
      city: wh.city,
      address: { ...wh.address },
      contactPerson: wh.contactPerson || '',
      contactPhone: wh.contactPhone || '',
      racks: (wh.racks || []).join(','),
    });
    setShowForm(true);
  };

  const handleToggle = async (wh) => {
    try {
      await api.put(`/warehouses/${wh._id}`, { isActive: !wh.isActive });
      toast.success(`Warehouse ${wh.isActive ? 'deactivated' : 'activated'}`);
      refetchWarehouses();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update warehouse');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const payload = {
      ...form,
      racks: form.racks.split(',').map((r) => r.trim().toUpperCase()).filter(Boolean),
    };
    try {
      if (editTarget) {
        await api.put(`/warehouses/${editTarget._id}`, payload);
        toast.success('Warehouse updated');
      } else {
        await api.post('/warehouses', payload);
        toast.success('Warehouse created');
      }
      setShowForm(false);
      refetchWarehouses();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save warehouse');
    } finally {
      setSubmitting(false);
    }
  };

  const setAddr = (key, val) => setForm((f) => ({ ...f, address: { ...f.address, [key]: val } }));

  return (
    <div className="space-y-5">
      {/* Action bar */}
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm text-zip-muted cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
            className="accent-zip-red"
          />
          Show inactive
        </label>
        {canManage && (
          <button onClick={openCreate} className="btn-primary flex items-center gap-2">
            <Plus size={15} /> Add Warehouse
          </button>
        )}
      </div>

      {/* Grid */}
      {displayed.length === 0 ? (
        <div className="zip-card p-10 text-center text-zip-muted text-sm">
          No warehouses found. Click <strong>Add Warehouse</strong> to create your first location.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {displayed.map((wh) => (
            <WarehouseCard key={wh._id} wh={wh} onEdit={openEdit} onToggle={handleToggle} canManage={canManage} />
          ))}
        </div>
      )}

      {/* Create / Edit modal */}
      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        title={editTarget ? 'Edit Warehouse' : 'Add Warehouse'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name & Code */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Name *</label>
              <input className="zip-input" required placeholder="Main Warehouse"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Code *</label>
              <input className="zip-input uppercase" required placeholder="WH01" maxLength={10}
                value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                disabled={!!editTarget} />
            </div>
          </div>

          {/* City */}
          <div>
            <label className="block text-xs font-medium text-zip-muted mb-1.5">City *</label>
            <input className="zip-input" required placeholder="Vadodara"
              value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          </div>

          {/* Address */}
          <div>
            <label className="block text-xs font-medium text-zip-muted mb-1.5">Street Address</label>
            <input className="zip-input" placeholder="Industrial Area, Phase 2"
              value={form.address.street} onChange={(e) => setAddr('street', e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">State</label>
              <input className="zip-input" placeholder="Gujarat"
                value={form.address.state} onChange={(e) => setAddr('state', e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Pincode</label>
              <input className="zip-input font-mono" placeholder="390010"
                value={form.address.pincode} onChange={(e) => setAddr('pincode', e.target.value)} />
            </div>
          </div>

          {/* Contact */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Contact Person</label>
              <input className="zip-input" placeholder="John Doe"
                value={form.contactPerson} onChange={(e) => setForm({ ...form, contactPerson: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Phone</label>
              <input className="zip-input font-mono" placeholder="9000000000"
                value={form.contactPhone} onChange={(e) => setForm({ ...form, contactPhone: e.target.value })} />
            </div>
          </div>

          {/* Racks */}
          <div>
            <label className="block text-xs font-medium text-zip-muted mb-1.5">Rack Sections <span className="text-zip-muted/60">(comma-separated)</span></label>
            <input className="zip-input font-mono uppercase" placeholder="A,B,C"
              value={form.racks} onChange={(e) => setForm({ ...form, racks: e.target.value.toUpperCase() })} />
            <p className="text-[11px] text-zip-muted/70 mt-1">e.g. A,B,C or WH-A,WH-B</p>
          </div>

          <div className="flex gap-3 pt-1">
            <button type="submit" disabled={submitting}
              className="btn-primary flex-1 flex items-center justify-center gap-2">
              {submitting
                ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Saving…</>
                : editTarget ? 'Save Changes' : 'Create Warehouse'}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="btn-ghost">Cancel</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default WarehouseList;
